import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"

import { HpiDataTable } from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { addDays, formatDate, formatUSDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface PayPeriodEmployee {
  user_id: number | null
  name: string
  pay_rate: number | null
  pay_type: number | null
  worker_type: number | null
  otj_minutes: number
  travel_minutes: number
  regular_minutes: number
  overtime_minutes: number
  time_pay: number
  flat_rate_total: number
  mileage: number
  mileage_total: number
  expenses: number
  expense_reimbursement_total: number
  expense_reimbursement_count: number
  expense_reimbursement_paid_total: number
  expense_reimbursement_paid_count: number
  mileage_paid: number
  mileage_paid_total: number
  mileage_unpaid: number
  mileage_unpaid_total: number
  time_pay_paid: number
  time_pay_unpaid: number
  pto_hours: number
  sto_hours: number
  unavail_days: number
  week1_otj_minutes: number
  week1_travel_minutes: number
  week1_regular_minutes: number
  week1_overtime_minutes: number
  week1_pto_hours: number
  week1_sto_hours: number
  week1_unavail_days: number
  week2_otj_minutes: number
  week2_travel_minutes: number
  week2_regular_minutes: number
  week2_overtime_minutes: number
  week2_pto_hours: number
  week2_sto_hours: number
  week2_unavail_days: number
  has_unapproved: boolean
}

interface PayPeriod {
  calc_type: number
  method_description: string
  week1_start: string
  week1_end: string
  week1_number: number
  week2_start: string
  week2_end: string
  week2_number: number
  employees: PayPeriodEmployee[]
  totals: PayPeriodEmployee | null
  unpaid_time_entry_count: number
  unpaid_expense_count: number
}

interface PendingTimeEntry {
  id: number
  date: string | null
  employee: string | null
  case_id: number | null
  case_name: string | null
  hours: number | null
  type: string | null
  description: string | null
  mileage: number | null
  date_added: string | null
}

interface PendingExpense {
  id: number
  date: string | null
  employee: string | null
  case_id: number | null
  case_name: string | null
  amount: number
  type: string | null
  description: string | null
  paid_to: string | null
  date_added: string | null
}

interface LogNote {
  id: number
  date: string | null
  employee: string | null
  content: string
  hidden: boolean
}

interface Props {
  pay_period: PayPeriod
  pending: {
    unapproved_time_entries: PendingTimeEntry[]
    unapproved_expenses: PendingExpense[]
  }
  log_notes: LogNote[]
}

/* ── Helpers ────────────────────────────────────────────── */

function TimeDisplay({ minutes }: { minutes: number }) {
  if (minutes === 0) return null
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return (
    <span className="tabular-nums">
      {h > 0 && <>{h}h </>}
      <span className={m === 0 ? "text-hpi-med-gray" : ""}>
        {String(m).padStart(2, "0")}m
      </span>
    </span>
  )
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount)
}

function TimePaidIndicator({ paid, unpaid }: { paid: number; unpaid: number }) {
  if (paid <= 0 && unpaid <= 0) return null
  const fullyPaid = paid > 0 && unpaid <= 0
  const partial = paid > 0 && unpaid > 0
  if (!fullyPaid && !partial) return null
  const tooltip = partial
    ? `${formatCurrency(paid)} paid / ${formatCurrency(unpaid)} pending`
    : `${formatCurrency(paid)} paid`
  return (
    <span
      className={`ml-1 inline-block h-1.5 w-1.5 rounded-full align-middle ${
        fullyPaid ? "bg-emerald-500" : "bg-amber-500"
      }`}
      title={tooltip}
    />
  )
}

function PaidBadge({ state }: { state: "paid" | "partial" }) {
  if (state === "paid") {
    return (
      <span
        className="ml-1 inline-flex items-center rounded bg-emerald-100 px-1 py-0 text-[9px] font-semibold text-emerald-700 uppercase"
        title="All paid"
      >
        ✓ Paid
      </span>
    )
  }
  return (
    <span
      className="ml-1 inline-flex items-center rounded bg-amber-100 px-1 py-0 text-[9px] font-semibold text-amber-700 uppercase"
      title="Some paid, some pending"
    >
      ◐ Partial
    </span>
  )
}

function MileageCell({
  miles,
  total,
  paidMiles,
  paidTotal,
  unpaidMiles,
  unpaidTotal,
}: {
  miles: number
  total: number
  paidMiles: number
  paidTotal: number
  unpaidMiles: number
  unpaidTotal: number
}) {
  if (!miles || miles <= 0) {
    return <span className="text-hpi-med-gray">—</span>
  }
  const hasPaid = paidMiles > 0
  const hasUnpaid = unpaidMiles > 0
  const fullyPaid = hasPaid && !hasUnpaid
  const partial = hasPaid && hasUnpaid
  return (
    <div className="leading-tight tabular-nums">
      <div className={fullyPaid ? "text-hpi-med-gray" : ""}>
        {miles} mi
        <span className="text-hpi-med-gray"> · </span>
        {formatCurrency(total)}
        {fullyPaid && <PaidBadge state="paid" />}
        {partial && <PaidBadge state="partial" />}
      </div>
      {partial && (
        <div className="text-hpi-med-gray text-[10px]">
          {formatCurrency(paidTotal)} paid / {formatCurrency(unpaidTotal)}{" "}
          pending
        </div>
      )}
    </div>
  )
}

function markPaidButtonLabel(pp: PayPeriod): string {
  const parts: string[] = []
  if (pp.unpaid_time_entry_count > 0) {
    parts.push(`${pp.unpaid_time_entry_count} W2 Time Entries`)
  }
  if (pp.unpaid_expense_count > 0) {
    parts.push(
      `${pp.unpaid_expense_count} ${
        pp.unpaid_expense_count === 1 ? "Expense" : "Expenses"
      }`,
    )
  }
  const subject = parts.join(" + ")
  return `Mark ${subject} from ${formatUSDate(pp.week1_start)} to ${formatUSDate(pp.week2_end)} Paid`
}

function ReimbursementCell({
  unpaidCount,
  unpaidTotal,
  paidCount,
  paidTotal,
}: {
  unpaidCount: number
  unpaidTotal: number
  paidCount: number
  paidTotal: number
}) {
  const totalCount = unpaidCount + paidCount
  const totalAmount = unpaidTotal + paidTotal
  if (totalCount <= 0) {
    return <span className="text-hpi-med-gray">—</span>
  }
  const fullyPaid = paidCount > 0 && unpaidCount === 0
  const partial = paidCount > 0 && unpaidCount > 0
  return (
    <div className="leading-tight tabular-nums">
      <div className={fullyPaid ? "text-hpi-med-gray" : ""}>
        {totalCount} {totalCount === 1 ? "exp" : "exps"}
        <span className="text-hpi-med-gray"> · </span>
        {formatCurrency(totalAmount)}
        {fullyPaid && <PaidBadge state="paid" />}
        {partial && <PaidBadge state="partial" />}
      </div>
      {partial && (
        <div className="text-hpi-med-gray text-[10px]">
          {formatCurrency(paidTotal)} paid / {formatCurrency(unpaidTotal)}{" "}
          pending
        </div>
      )}
    </div>
  )
}

/* ── Pending Columns ──────────────────────────────────── */

const pendingTimeColumns: HpiColumn<PendingTimeEntry>[] = [
  {
    header: "Date",
    width: "max-w-[120px]",
    render: (row) => (
      <div className="text-sm">
        {row.case_id ? (
          <a
            href={`/cases/${row.case_id}/time_entries/${row.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-hpi-active-blue hover:underline"
          >
            {formatDate(row.date)}
          </a>
        ) : (
          formatDate(row.date)
        )}
      </div>
    ),
  },
  {
    header: "Employee",
    render: (row) => <div className="text-sm">{row.employee ?? "-"}</div>,
  },
  {
    header: "Case",
    render: (row) => <div className="text-sm">{row.case_name ?? "-"}</div>,
  },
  {
    header: "Type",
    render: (row) => <div className="text-sm">{row.type ?? "-"}</div>,
  },
  {
    header: "Description",
    render: (row) => {
      const parts: string[] = []
      if (row.description) parts.push(row.description)
      if (row.mileage && row.mileage > 0) parts.push(`${row.mileage} miles`)
      return (
        <div className="max-w-[200px] truncate text-sm">
          {parts.length > 0 ? parts.join(", ") : "-"}
        </div>
      )
    },
  },
  {
    header: "Hours",
    width: "max-w-[80px]",
    render: (row) => (
      <div className="text-right text-sm">{row.hours?.toFixed(2) ?? "-"}</div>
    ),
  },
  {
    header: "Added",
    width: "max-w-[120px]",
    render: (row) => (
      <div className="text-sm">{formatDate(row.date_added)}</div>
    ),
  },
]

const pendingExpenseColumns: HpiColumn<PendingExpense>[] = [
  {
    header: "Date",
    width: "max-w-[120px]",
    render: (row) => (
      <div className="text-sm">
        {row.case_id ? (
          <a
            href={`/cases/${row.case_id}/expenses/${row.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-hpi-active-blue hover:underline"
          >
            {formatDate(row.date)}
          </a>
        ) : (
          formatDate(row.date)
        )}
      </div>
    ),
  },
  {
    header: "Employee",
    render: (row) => <div className="text-sm">{row.employee ?? "-"}</div>,
  },
  {
    header: "Case",
    render: (row) => <div className="text-sm">{row.case_name ?? "-"}</div>,
  },
  {
    header: "Type",
    render: (row) => <div className="text-sm">{row.type ?? "-"}</div>,
  },
  {
    header: "Description",
    render: (row) => {
      const parts: string[] = []
      if (row.description) parts.push(row.description)
      if (row.paid_to) parts.push(row.paid_to)
      return (
        <div className="max-w-[200px] truncate text-sm">
          {parts.length > 0 ? parts.join(", ") : "-"}
        </div>
      )
    },
  },
  {
    header: "Amount",
    width: "max-w-[100px]",
    render: (row) => (
      <div className="text-right text-sm">{formatCurrency(row.amount)}</div>
    ),
  },
  {
    header: "Added",
    width: "max-w-[120px]",
    render: (row) => (
      <div className="text-sm">{formatDate(row.date_added)}</div>
    ),
  },
]

/* ── Table Components ─────────────────────────────────── */

function TableHeader({ label }: { label: string }) {
  return (
    <tr>
      <td
        colSpan={9}
        className="bg-hpi-dark-blue rounded-t px-4 py-2.5 text-sm font-semibold tracking-wider text-white uppercase"
      >
        {label}
      </td>
    </tr>
  )
}

function PeriodColumnHeaders() {
  return (
    <tr className="border-hpi-light-gray text-hpi-active-blue border-b bg-white text-center text-[11px] font-medium">
      <th className="border-hpi-light-gray border-r-2 px-2 py-2 text-left font-medium">
        Employee
      </th>
      <th className="px-2 py-2 font-medium">OTJ</th>
      <th className="px-2 py-2 font-medium">Travel</th>
      <th className="border-hpi-light-gray border-r-2 px-2 py-2 font-medium">
        Total Hours
      </th>
      <th className="px-2 py-2 font-medium">Reg</th>
      <th className="border-hpi-light-gray border-r-2 px-2 py-2 font-medium">
        OT
      </th>
      <th className="px-2 py-2 font-medium">Mileage</th>
      <th className="border-hpi-light-gray border-r-2 px-2 py-2 leading-tight font-medium">
        Approved
        <br />
        Reimb.
      </th>
      <th className="border-hpi-light-gray border-l-2 px-1 py-2 leading-tight font-medium">
        Unavail
        <br />
        days
      </th>
    </tr>
  )
}

function WeekColumnHeaders() {
  return (
    <tr className="border-hpi-light-gray text-hpi-active-blue border-b bg-white text-center text-[11px] font-medium">
      <th className="border-hpi-light-gray border-r-2 px-2 py-2 text-left font-medium">
        Employee
      </th>
      <th className="px-2 py-2 font-medium">OTJ</th>
      <th className="px-2 py-2 font-medium">Travel</th>
      <th className="border-hpi-light-gray border-r-2 px-2 py-2 font-medium">
        Total Hours
      </th>
      <th className="px-2 py-2 font-medium" />
      <th className="border-hpi-light-gray border-r-2 px-2 py-2 font-medium" />
      <th className="px-2 py-2 font-medium" />
      <th className="border-hpi-light-gray border-r-2 px-2 py-2 font-medium" />
      <th className="border-hpi-light-gray border-l-2 px-1 py-2 leading-tight font-medium">
        Unavail
        <br />
        days
      </th>
    </tr>
  )
}

function PeriodRow({
  emp,
  reportStart,
  reportEnd,
}: {
  emp: PayPeriodEmployee
  reportStart: string
  reportEnd: string
}) {
  const totalMinutes = emp.otj_minutes + emp.travel_minutes

  return (
    <tr
      className={`border-hpi-light-gray/50 text-hpi-dark-gray border-b text-sm transition-colors hover:shadow-[inset_4px_0_0_0_var(--color-hpi-active-blue)] ${
        emp.has_unapproved
          ? "bg-hpi-light-red/30 hover:bg-hpi-light-red/60"
          : "hover:bg-hpi-pale-gray bg-white"
      }`}
    >
      <td className="border-hpi-light-gray border-r-2 px-2 py-2 text-left font-semibold">
        {emp.user_id ? (
          <a
            href={`/team/activity?employee_id=${emp.user_id}&start_date=${reportStart}&end_date=${reportEnd}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-hpi-dark-gray hover:text-hpi-active-blue"
          >
            {emp.name}
            {emp.pay_type != null && (
              <span className="ml-1.5 text-[10px] font-bold">
                <span
                  className={
                    emp.pay_type === 1
                      ? "text-amber-600"
                      : "text-hpi-active-blue"
                    /* 0=Salary, 1=Hourly */
                  }
                >
                  {emp.pay_type === 0 ? "SAL" : "HR"}
                </span>
                /
                <span
                  className={
                    emp.worker_type === 1
                      ? "text-amber-600"
                      : "text-hpi-active-blue"
                  }
                >
                  {emp.worker_type === 1 ? "1099" : "W2"}
                </span>
              </span>
            )}
          </a>
        ) : (
          <span>{emp.name}</span>
        )}
      </td>
      <td className="px-2 py-2 text-right">
        <TimeDisplay minutes={emp.otj_minutes} />
      </td>
      <td className="px-2 py-2 text-right">
        <TimeDisplay minutes={emp.travel_minutes} />
      </td>
      <td className="border-hpi-light-gray border-r-2 px-2 py-2 text-right">
        <TimeDisplay minutes={totalMinutes} />
      </td>
      <td className="px-2 py-2 text-right font-bold">
        <TimeDisplay minutes={emp.regular_minutes} />
        <TimePaidIndicator
          paid={emp.time_pay_paid}
          unpaid={emp.time_pay_unpaid}
        />
      </td>
      <td className="text-hpi-red border-hpi-light-gray border-r-2 px-2 py-2 text-right font-bold">
        <TimeDisplay minutes={emp.overtime_minutes} />
      </td>
      <td className="px-2 py-2 text-right text-xs">
        <MileageCell
          miles={emp.mileage}
          total={emp.mileage_total}
          paidMiles={emp.mileage_paid}
          paidTotal={emp.mileage_paid_total}
          unpaidMiles={emp.mileage_unpaid}
          unpaidTotal={emp.mileage_unpaid_total}
        />
      </td>
      <td className="border-hpi-light-gray border-r-2 px-2 py-2 text-right text-xs">
        <ReimbursementCell
          unpaidCount={emp.expense_reimbursement_count}
          unpaidTotal={emp.expense_reimbursement_total}
          paidCount={emp.expense_reimbursement_paid_count}
          paidTotal={emp.expense_reimbursement_paid_total}
        />
      </td>
      <td className="border-hpi-light-gray border-l-2 px-1 py-2 text-right">
        {emp.unavail_days > 0 ? Math.round(emp.unavail_days) : ""}
      </td>
    </tr>
  )
}

function TotalRow({ data }: { data: PayPeriodEmployee }) {
  const totalMinutes = data.otj_minutes + data.travel_minutes

  return (
    <tr className="border-hpi-light-gray text-hpi-dark-gray border-t-2 bg-white text-sm font-bold">
      <td className="border-hpi-light-gray border-r-2 px-2 py-2 text-left">
        Total
      </td>
      <td className="px-2 py-2 text-right">
        <TimeDisplay minutes={data.otj_minutes} />
      </td>
      <td className="px-2 py-2 text-right">
        <TimeDisplay minutes={data.travel_minutes} />
      </td>
      <td className="border-hpi-light-gray border-r-2 px-2 py-2 text-right">
        <TimeDisplay minutes={totalMinutes} />
      </td>
      <td className="px-2 py-2 text-right">
        <TimeDisplay minutes={data.regular_minutes} />
        <TimePaidIndicator
          paid={data.time_pay_paid}
          unpaid={data.time_pay_unpaid}
        />
      </td>
      <td className="text-hpi-red border-hpi-light-gray border-r-2 px-2 py-2 text-right">
        <TimeDisplay minutes={data.overtime_minutes} />
      </td>
      <td className="px-2 py-2 text-right text-xs">
        <MileageCell
          miles={data.mileage}
          total={data.mileage_total}
          paidMiles={data.mileage_paid}
          paidTotal={data.mileage_paid_total}
          unpaidMiles={data.mileage_unpaid}
          unpaidTotal={data.mileage_unpaid_total}
        />
      </td>
      <td className="border-hpi-light-gray border-r-2 px-2 py-2 text-right text-xs">
        <ReimbursementCell
          unpaidCount={data.expense_reimbursement_count}
          unpaidTotal={data.expense_reimbursement_total}
          paidCount={data.expense_reimbursement_paid_count}
          paidTotal={data.expense_reimbursement_paid_total}
        />
      </td>
      <td className="border-hpi-light-gray border-l-2 px-1 py-2 text-right">
        {data.unavail_days > 0 ? Math.round(data.unavail_days) : ""}
      </td>
    </tr>
  )
}

function WeekRow({
  emp,
  weekNum,
  weekStart,
}: {
  emp: PayPeriodEmployee
  weekNum: 1 | 2
  weekStart: string
}) {
  const otj = weekNum === 1 ? emp.week1_otj_minutes : emp.week2_otj_minutes
  const travel =
    weekNum === 1 ? emp.week1_travel_minutes : emp.week2_travel_minutes
  const unavail =
    weekNum === 1 ? emp.week1_unavail_days : emp.week2_unavail_days
  const totalMinutes = otj + travel

  return (
    <tr
      className={`border-hpi-light-gray/50 text-hpi-dark-gray border-b text-sm transition-colors hover:shadow-[inset_4px_0_0_0_var(--color-hpi-active-blue)] ${
        emp.has_unapproved
          ? "bg-hpi-light-red/30 hover:bg-hpi-light-red/60"
          : "hover:bg-hpi-pale-gray bg-white"
      }`}
    >
      <td className="border-hpi-light-gray border-r-2 px-2 py-2 text-left font-semibold">
        {emp.user_id ? (
          <a
            href={`/accounting/payroll/week_detail?employee_id=${emp.user_id}&start_date=${weekStart}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-hpi-dark-gray hover:text-hpi-active-blue"
          >
            {emp.name}
          </a>
        ) : (
          <span>{emp.name}</span>
        )}
      </td>
      <td className="px-2 py-2 text-right">
        <TimeDisplay minutes={otj} />
      </td>
      <td className="px-2 py-2 text-right">
        <TimeDisplay minutes={travel} />
      </td>
      <td className="border-hpi-light-gray border-r-2 px-2 py-2 text-right">
        <TimeDisplay minutes={totalMinutes} />
      </td>
      <td className="px-2 py-2" />
      <td className="border-hpi-light-gray border-r-2 px-2 py-2" />
      <td className="px-2 py-2" />
      <td className="border-hpi-light-gray border-r-2 px-2 py-2" />
      <td className="border-hpi-light-gray border-l-2 px-1 py-2 text-right">
        {unavail > 0 ? Math.round(unavail) : ""}
      </td>
    </tr>
  )
}

function WeekTotalRow({
  employees,
  weekNum,
}: {
  employees: PayPeriodEmployee[]
  weekNum: 1 | 2
}) {
  const otj = employees.reduce(
    (s, e) => s + (weekNum === 1 ? e.week1_otj_minutes : e.week2_otj_minutes),
    0,
  )
  const travel = employees.reduce(
    (s, e) =>
      s + (weekNum === 1 ? e.week1_travel_minutes : e.week2_travel_minutes),
    0,
  )
  const unavail = employees.reduce(
    (s, e) => s + (weekNum === 1 ? e.week1_unavail_days : e.week2_unavail_days),
    0,
  )
  const totalMinutes = otj + travel

  return (
    <tr className="border-hpi-light-gray text-hpi-dark-gray border-t-2 bg-white text-sm font-bold">
      <td className="border-hpi-light-gray border-r-2 px-2 py-2 text-left">
        Total
      </td>
      <td className="px-2 py-2 text-right">
        <TimeDisplay minutes={otj} />
      </td>
      <td className="px-2 py-2 text-right">
        <TimeDisplay minutes={travel} />
      </td>
      <td className="border-hpi-light-gray border-r-2 px-2 py-2 text-right">
        <TimeDisplay minutes={totalMinutes} />
      </td>
      <td className="px-2 py-2" />
      <td className="border-hpi-light-gray border-r-2 px-2 py-2" />
      <td className="px-2 py-2" />
      <td className="border-hpi-light-gray border-r-2 px-2 py-2" />
      <td className="border-hpi-light-gray border-l-2 px-1 py-2 text-right">
        {unavail > 0 ? Math.round(unavail) : ""}
      </td>
    </tr>
  )
}

const COL_WIDTHS = (
  <colgroup>
    <col style={{ width: "20%" }} />
    <col style={{ width: "9%" }} />
    <col style={{ width: "9%" }} />
    <col style={{ width: "10%" }} />
    <col style={{ width: "9%" }} />
    <col style={{ width: "9%" }} />
    <col style={{ width: "14%" }} />
    <col style={{ width: "13%" }} />
    <col style={{ width: "7%" }} />
  </colgroup>
)

/* ── Main Component ────────────────────────────────────── */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Accounting", href: "/accounting/expenses" },
  { title: "Payroll", href: "/accounting/payroll" },
]

export default function PayrollCurrent({
  pay_period,
  pending,
  log_notes,
}: Props) {
  const [endDate, setEndDate] = useState(pay_period.week2_end)

  const handleChangeDateRange = () => {
    router.get("/accounting/payroll", { end_date: endDate })
  }

  const handleMarkPaid = () => {
    if (!confirm("Are you sure you want to mark these entries as paid?")) return
    router.post("/accounting/payroll/mark_paid", {
      start_date: pay_period.week1_start,
      end_date: pay_period.week2_end,
    })
  }

  const prevEndDate = addDays(pay_period.week2_end, -14)
  const nextEndDate = addDays(pay_period.week2_end, 14)

  const periodEmployees = pay_period.employees.filter(
    (e) => e.otj_minutes > 0 || e.travel_minutes > 0 || e.unavail_days > 0,
  )

  const week1Employees = pay_period.employees.filter(
    (e) =>
      e.week1_otj_minutes > 0 ||
      e.week1_travel_minutes > 0 ||
      e.week1_unavail_days > 0,
  )
  const week2Employees = pay_period.employees.filter(
    (e) =>
      e.week2_otj_minutes > 0 ||
      e.week2_travel_minutes > 0 ||
      e.week2_unavail_days > 0,
  )

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Payroll Report" />

      <div className="p-6">
        {/* Simple header matching legacy */}
        <div className="mb-4">
          <h3 className="text-hpi-dark-gray text-lg font-semibold">
            Payroll Report
          </h3>
          <div className="text-hpi-dark-gray text-sm">
            {formatUSDate(pay_period.week1_start)} to{" "}
            {formatUSDate(pay_period.week2_end)}
          </div>
          {log_notes.length > 0 && (
            <a
              href="#logs"
              className="text-hpi-active-blue text-xs hover:underline"
            >
              {log_notes.length} Log Note{log_notes.length !== 1 ? "s" : ""}
            </a>
          )}
        </div>

        {/* Previous / Next Navigation (top) */}
        <div className="text-hpi-dark-gray mb-4 text-center text-sm">
          <span>&laquo; </span>
          <Link
            href={`/accounting/payroll?end_date=${prevEndDate}`}
            className="text-hpi-active-blue hover:underline"
          >
            Previous Billing Period
          </Link>
          <span className="mx-1">|</span>
          <Link
            href={`/accounting/payroll?end_date=${nextEndDate}`}
            className="text-hpi-active-blue hover:underline"
          >
            Next Billing Period
          </Link>
          <span> &raquo;</span>
        </div>

        {/* Date Range Picker */}
        <div className="border-hpi-light-gray mb-6 flex items-end gap-3 rounded-lg border bg-white px-4 py-3">
          <div>
            <label className="text-hpi-active-blue mb-1 block text-xs font-medium">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border-hpi-light-gray focus:border-hpi-active-blue rounded border px-3 py-1.5 text-sm focus:outline-none"
            />
          </div>
          <button
            onClick={handleChangeDateRange}
            className="bg-hpi-dark-green rounded px-4 py-1.5 text-sm font-medium text-white hover:opacity-90"
          >
            Change Date Range
          </button>
        </div>

        {/* Unapproved Time Entries */}
        {pending.unapproved_time_entries.length > 0 && (
          <div className="mb-6">
            <HpiDataTable<PendingTimeEntry>
              title={`Unapproved Time Entries (${pending.unapproved_time_entries.length})`}
              columns={pendingTimeColumns}
              data={pending.unapproved_time_entries}
              keyFn={(row) => row.id}
            />
          </div>
        )}

        {/* Unapproved Expenses */}
        {pending.unapproved_expenses.length > 0 && (
          <div className="mb-6">
            <HpiDataTable<PendingExpense>
              title={`Unapproved Expenses (${pending.unapproved_expenses.length})`}
              columns={pendingExpenseColumns}
              data={pending.unapproved_expenses}
              keyFn={(row) => row.id}
            />
          </div>
        )}

        {/* Tables */}
        {periodEmployees.length > 0 && (
          <div className="space-y-6">
            {/* Total for Pay Period */}
            <div className="border-hpi-light-gray overflow-hidden rounded-lg border">
              <table className="w-full border-collapse">
                {COL_WIDTHS}
                <thead>
                  <TableHeader
                    label={`Total for Pay Period ${formatUSDate(pay_period.week1_start)} to ${formatUSDate(pay_period.week2_end)}`}
                  />
                  <PeriodColumnHeaders />
                </thead>
                <tbody>
                  {periodEmployees.map((emp) => (
                    <PeriodRow
                      key={emp.user_id ?? "total"}
                      emp={emp}
                      reportStart={pay_period.week1_start}
                      reportEnd={pay_period.week2_end}
                    />
                  ))}
                  {pay_period.totals && <TotalRow data={pay_period.totals} />}
                </tbody>
              </table>
            </div>

            {/* Week 1 */}
            <div className="border-hpi-light-gray overflow-hidden rounded-lg border">
              <table className="w-full border-collapse">
                {COL_WIDTHS}
                <thead>
                  <TableHeader
                    label={`Week ${pay_period.week1_number}: ${formatUSDate(pay_period.week1_start)} to ${formatUSDate(pay_period.week1_end)}`}
                  />
                  <WeekColumnHeaders />
                </thead>
                <tbody>
                  {week1Employees.map((emp) => (
                    <WeekRow
                      key={emp.user_id ?? "w1-total"}
                      emp={emp}
                      weekNum={1}
                      weekStart={pay_period.week1_start}
                    />
                  ))}
                  <WeekTotalRow employees={pay_period.employees} weekNum={1} />
                </tbody>
              </table>
            </div>

            {/* Week 2 */}
            <div className="border-hpi-light-gray overflow-hidden rounded-lg border">
              <table className="w-full border-collapse">
                {COL_WIDTHS}
                <thead>
                  <TableHeader
                    label={`Week ${pay_period.week2_number}: ${formatUSDate(pay_period.week2_start)} to ${formatUSDate(pay_period.week2_end)}`}
                  />
                  <WeekColumnHeaders />
                </thead>
                <tbody>
                  {week2Employees.map((emp) => (
                    <WeekRow
                      key={emp.user_id ?? "w2-total"}
                      emp={emp}
                      weekNum={2}
                      weekStart={pay_period.week2_start}
                    />
                  ))}
                  <WeekTotalRow employees={pay_period.employees} weekNum={2} />
                </tbody>
              </table>
            </div>
          </div>
        )}

        {periodEmployees.length === 0 && (
          <div className="border-hpi-light-gray text-hpi-med-blue rounded-lg border bg-white p-8 text-center">
            No payroll data found for this period.
          </div>
        )}

        {/* Mark Paid */}
        {(pay_period.unpaid_time_entry_count > 0 ||
          pay_period.unpaid_expense_count > 0) && (
          <div className="mt-8 text-center">
            <button
              onClick={handleMarkPaid}
              className="bg-hpi-dark-green rounded px-6 py-2.5 text-sm font-medium text-white hover:opacity-90"
            >
              {markPaidButtonLabel(pay_period)}
            </button>
          </div>
        )}

        {/* Previous / Next Navigation */}
        <div className="text-hpi-dark-gray mt-6 text-center text-sm">
          <span>&laquo; </span>
          <Link
            href={`/accounting/payroll?end_date=${prevEndDate}`}
            className="text-hpi-active-blue hover:underline"
          >
            Previous Billing Period
          </Link>
          <span className="mx-1">|</span>
          <Link
            href={`/accounting/payroll?end_date=${nextEndDate}`}
            className="text-hpi-active-blue hover:underline"
          >
            Next Billing Period
          </Link>
          <span> &raquo;</span>
        </div>

        {/* Log Notes */}
        {log_notes.length > 0 && (
          <div id="logs" className="mt-8">
            <div className="border-hpi-light-gray mx-auto max-w-3xl rounded-lg border bg-white p-5">
              <h3 className="text-hpi-dark-gray mb-4 text-base font-semibold">
                Log Entries
                <Link
                  href="/reports/employee_logs"
                  className="text-hpi-active-blue ml-2 text-xs font-normal hover:underline"
                >
                  View All
                </Link>
              </h3>
              <div className="space-y-3">
                {log_notes.map((note) => (
                  <div
                    key={note.id}
                    className={note.hidden ? "text-hpi-med-gray" : ""}
                  >
                    <div className="text-sm font-bold">
                      {note.date ? formatUSDate(note.date) : ""} -{" "}
                      {note.employee ?? ""}
                    </div>
                    <div className="text-xs leading-relaxed whitespace-pre-wrap">
                      {note.content}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
